import React from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import DatePicker from '../../Common/DatePicker/DatePicker';
import Toggle from '../../Common/Toggle/Toggle';
import { getDateValue } from '../../../helpers/reports';
import '../Reports.scss';

export class CoachSummaryFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            locationKeys: '',
            coachId: '',
            targetDateFrom: '',
            targetDateTo: '',
            isProjectRetired: false
        }

        this.state = {
            emptyForm: initialFormSelections,
            locationKeys: initialFormSelections.locationKeys,
            coachId: initialFormSelections.coachId,
            targetDateFrom: initialFormSelections.targetDateFrom,
            targetDateTo: initialFormSelections.targetDateTo,
            isProjectRetired: initialFormSelections.isProjectRetired
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,
                locationKeys: appState.locationKeys ? appState.locationKeys : newState.locationKeys,
                coachId: appState.coachId ? appState.coachId : newState.coachId,
                targetDateFrom: appState.targetDateFrom ? appState.targetDateFrom : newState.targetDateFrom,
                targetDateTo: appState.targetDateTo ? appState.targetDateTo : newState.targetDateTo,
                isProjectRetired: appState.isProjectRetired ? appState.isProjectRetired : newState.isProjectRetired
            }
        }

        this.setState({
            ...this.state,
            ...newState
        });

        if (newState.coachId) {
            this.onCoachChange(newState.coachId, newState.locationKeys);
        }
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        if (!this.state.locationKeys || this.state.locationKeys.length <= 0
            || !this.state.coachId || this.state.coachId <= 0) {
            return false;
        }

        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.locationKeys = (reportRequest.locationKeys) ? reportRequest.locationKeys.join(',') : form.locationKeys;
        form.targetDateFrom = getDateValue(reportRequest.targetDateFrom);
        form.targetDateTo = getDateValue(reportRequest.targetDateTo);
        form.coachId = (reportRequest.coachId) ? reportRequest.coachId : form.coachId;
        form.isProjectRetired = (reportRequest.shouldIncludeRetiredProjects === true) ? true : false;

        this.setState({
            ...this.state,
            ...form
        });
    }

    mapToApiRequest = () => {
        const request = {
            reportId: this.props.report.id,
            locationKeys: this.state.locationKeys.split(','),
            targetDateFrom: this.state.targetDateFrom,
            targetDateTo: this.state.targetDateTo,
            coachId: this.state.coachId,
            shouldIncludeRetiredProjects: this.state.isProjectRetired
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({ ...this.state, [name]: value });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onCoachChange = (coachId, selectedLocations) => {
        this.setState({
            ...this.state,
            coachId: coachId,
            locationKeys: selectedLocations || []
        });

        if (coachId) {
            this.props.onCoachChange(coachId);
        }
    }

    onDatePickerChange = (date, name) => {
        this.onFieldChange(name, date);
    }

    render() {
        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="coachId"
                            label="Coach" idField="id" valueField="name"
                            options={this.props.filters.coaches}
                            selectedValues={this.state.coachId.toString()}
                            onChange={this.onCoachChange}
                            isRequired={true}
                            errorMessage="Coach is required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="locationKeys"
                            label="Location" idField="id" valueField="name"
                            options={this.props.filters.coachLocations || []}
                            selectedValues={this.state.locationKeys.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={true}
                            errorMessage="Location is required"
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-2">
                        <DatePicker showIcon name="targetDateFrom"
                            value={this.state.targetDateFrom}
                            label="Target Date From"
                            onChange={this.onDatePickerChange} />
                    </div>
                    <div className="col-sm-12 col-md-2">
                        <DatePicker showIcon name="targetDateTo"
                            value={this.state.targetDateTo}
                            label="To"
                            onChange={this.onDatePickerChange} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <Toggle name="isProjectRetired"
                            text="Include Retired Projects"
                            checked={this.state.isProjectRetired}
                            onChange={this.onFieldChange} />
                    </div>
                </div>
            </form>
        );
    }
}

CoachSummaryFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    registerReportFilterFunctions: PropTypes.func.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    onCoachChange: PropTypes.func.isRequired
};

export default CoachSummaryFilters;
